/**
 * Lead detail drawer (#21 AC #6) — opens on the right when an operator
 * clicks a row in the leads table. Fetches the full lead via
 * `GET /api/admin/leads/:id` (provides `formJson` + variant join the
 * list response omits).
 *
 * Sections:
 *   - Header   — name, email, status chip.
 *   - Form data — every `formJson` field rendered as a read-only TextField.
 *   - Notes    — editable multiline TextField with Save button (PATCH).
 *   - Status   — Select of allowed next statuses (computed from
 *                `LEAD_STATUS_TRANSITIONS`); Save opens a confirm dialog
 *                before issuing the PATCH. On 422 INVALID_LEAD_TRANSITION
 *                we render the `details.allowed` list as a hint.
 *
 * Responsive:
 *   - Drawer fills full width on mobile (xs/sm), 480px on desktop.
 */

import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  LEAD_STATUS_TRANSITIONS,
  type LeadAdminResponse,
  type LeadStatus,
} from "@ottopilot/shared";
import { useLeadDetail, useUpdateLead } from "../../hooks/useLeads";
import { useToast } from "../../components/ToastProvider";
import { ApiClientError } from "../../lib/api";
import { LeadStatusChip } from "./leadStatusChip";

export type LeadDetailDrawerProps = {
  leadId: string | null;
  onClose: () => void;
};

/**
 * Render the `formJson` payload as a stack of read-only TextFields.
 * Permissive: we accept any record shape the operator may have configured
 * server-side. Non-string values are JSON-stringified for display so the
 * drawer doesn't crash on a numeric `age` or a checkbox array.
 */
function FormDataReadout({ formJson }: { formJson: unknown }): ReactElement {
  if (!formJson || typeof formJson !== "object") {
    return (
      <Typography variant="body2" color="text.secondary">
        No structured form data captured.
      </Typography>
    );
  }
  const entries = Object.entries(formJson as Record<string, unknown>);
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No form fields submitted.
      </Typography>
    );
  }
  return (
    <Stack spacing={1.5} data-testid="lead-detail-form-fields">
      {entries.map(([key, value]) => {
        const display = typeof value === "string" ? value : JSON.stringify(value);
        return (
          <TextField
            key={key}
            label={key}
            value={display}
            size="small"
            fullWidth
            slotProps={{ input: { readOnly: true } }}
            data-testid={`lead-detail-form-field-${key}`}
          />
        );
      })}
    </Stack>
  );
}

function LeadDetailContent({
  lead,
  onClose,
}: {
  lead: LeadAdminResponse;
  onClose: () => void;
}): ReactElement {
  const toast = useToast();
  const update = useUpdateLead(lead.id);

  // Drawer-local edit state. Reset whenever the upstream lead changes
  // (e.g. after a successful save we re-pull from the server).
  const [notesDraft, setNotesDraft] = useState(lead.notes ?? "");
  const [statusDraft, setStatusDraft] = useState<LeadStatus>(lead.status);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Server-emitted hint for an invalid transition. Sourced from
  // ApiClientError.details.allowed and rendered as an Alert.
  const [transitionHint, setTransitionHint] = useState<ReadonlyArray<LeadStatus> | null>(null);

  useEffect(() => {
    setNotesDraft(lead.notes ?? "");
    setStatusDraft(lead.status);
    setTransitionHint(null);
  }, [lead.id, lead.notes, lead.status]);

  const allowedNext = useMemo<ReadonlyArray<LeadStatus>>(
    () => LEAD_STATUS_TRANSITIONS[lead.status],
    [lead.status],
  );

  const notesChanged = notesDraft !== (lead.notes ?? "");
  const statusChanged = statusDraft !== lead.status;

  const handleSaveNotes = () => {
    if (!notesChanged) return;
    update.mutate(
      { notes: notesDraft },
      {
        onSuccess: () => toast.showSuccess("Notes saved."),
        onError: (err) => toast.showError(err.message || "Could not save notes."),
      },
    );
  };

  const handleConfirmStatus = () => {
    setConfirmOpen(false);
    update.mutate(
      { status: statusDraft },
      {
        onSuccess: () => {
          setTransitionHint(null);
          toast.showSuccess(`Lead marked as ${statusDraft}.`);
        },
        onError: (err) => {
          if (err instanceof ApiClientError && err.code === "INVALID_LEAD_TRANSITION") {
            const details = err.details as { allowed?: ReadonlyArray<LeadStatus> } | undefined;
            if (details?.allowed) {
              setTransitionHint(details.allowed);
            }
          }
          toast.showError(err.message || "Could not update status.");
        },
      },
    );
  };

  return (
    <Box
      sx={{ width: { xs: "100vw", sm: 480 }, p: 3, height: "100%", overflowY: "auto" }}
      role="presentation"
      data-testid="lead-detail-drawer"
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h3" data-testid="lead-detail-name">
            {lead.name ?? "(unnamed)"}
          </Typography>
          {lead.email ? (
            <Typography variant="body2" color="text.secondary" data-testid="lead-detail-email">
              {lead.email}
            </Typography>
          ) : null}
          <Box sx={{ mt: 1 }}>
            <LeadStatusChip status={lead.status} />
          </Box>
        </Box>
        <IconButton
          aria-label="Close drawer"
          onClick={onClose}
          data-testid="lead-detail-close-button"
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={3}>
        {lead.experimentVariantName ? (
          <Box>
            <Typography variant="subtitle2">Experiment variant</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="lead-detail-variant-name"
            >
              {lead.experimentVariantName}
            </Typography>
          </Box>
        ) : null}

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Form data
          </Typography>
          <FormDataReadout formJson={lead.formJson} />
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Notes
          </Typography>
          <TextField
            multiline
            minRows={3}
            maxRows={10}
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            fullWidth
            placeholder="Internal notes — visible only to operators."
            data-testid="lead-detail-notes-input"
          />
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={!notesChanged || update.isPending}
              onClick={handleSaveNotes}
              data-testid="lead-detail-notes-save"
            >
              Save notes
            </Button>
          </Stack>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Status
          </Typography>
          {allowedNext.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {lead.status === "REJECTED"
                ? "Rejected leads cannot be re-opened."
                : "No further transitions available."}
            </Typography>
          ) : (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="stretch">
              <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                <InputLabel id="lead-detail-status-label">Change to</InputLabel>
                <Select<LeadStatus>
                  labelId="lead-detail-status-label"
                  label="Change to"
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value)}
                  data-testid="lead-detail-status-select"
                >
                  {/* The current status is the default; all allowed
                      transitions follow. */}
                  <MenuItem value={lead.status}>{lead.status} (current)</MenuItem>
                  {allowedNext.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disabled={!statusChanged || update.isPending}
                onClick={() => setConfirmOpen(true)}
                data-testid="lead-detail-status-apply"
              >
                Apply
              </Button>
            </Stack>
          )}
          {transitionHint ? (
            <Alert severity="warning" sx={{ mt: 1 }} data-testid="lead-detail-transition-hint">
              {transitionHint.length === 0
                ? "No transitions are currently allowed from this status."
                : `Allowed next statuses: ${transitionHint.join(", ")}`}
            </Alert>
          ) : null}
        </Box>

        <Divider />

        <Box>
          <Typography variant="caption" color="text.secondary">
            Captured: {new Date(lead.createdAt).toLocaleString()}
          </Typography>
        </Box>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Change lead status?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Move this lead from <strong>{lead.status}</strong> to <strong>{statusDraft}</strong>?
            This is recorded in the activity log.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmStatus}
            data-testid="lead-detail-status-confirm"
          >
            Apply change
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function LeadDetailDrawer({ leadId, onClose }: LeadDetailDrawerProps): ReactElement {
  const detail = useLeadDetail(leadId ?? undefined);

  return (
    <Drawer
      anchor="right"
      open={Boolean(leadId)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100vw", sm: 480 } } }}
    >
      {!leadId ? null : detail.isPending ? (
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 6, flex: 1 }}
        >
          <CircularProgress />
        </Box>
      ) : detail.isError ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" data-testid="lead-detail-error">
            {detail.error.message}
          </Alert>
        </Box>
      ) : detail.data ? (
        <LeadDetailContent lead={detail.data} onClose={onClose} />
      ) : null}
    </Drawer>
  );
}
